import dayjs, { type ManipulateType } from "dayjs";
import calendar from "dayjs/plugin/calendar";
import customParseFormat from "dayjs/plugin/customParseFormat";
import relativeTime from "dayjs/plugin/relativeTime";
import utc from "dayjs/plugin/utc";
import "dayjs/locale/en-gb";
import "dayjs/locale/de";
import "dayjs/locale/da";
import "dayjs/locale/fr";
import "dayjs/locale/it";
import "dayjs/locale/es";
import { formatLocaleShortDateTime, DEFAULT_LOCALE, type SupportedLocale } from "@patient-watch/i18n";

dayjs.extend(calendar);
dayjs.extend(customParseFormat);
dayjs.extend(utc);

// Extend dayjs with the relativeTime plugin
dayjs.extend(relativeTime);

const DAYJS_LOCALE: Record<SupportedLocale, string> = {
    "en-GB": "en-gb",
    "en-US": "en",
    "de-DE": "de",
    "da-DK": "da",
    "fr-FR": "fr",
    "it-IT": "it",
    "es-ES": "es",
};

/** Localised fallback shown when a date is missing, e.g. "Date not set" / "Datum nicht festgelegt". */
const DATE_NOT_SET: Record<SupportedLocale, string> = {
    "en-GB": "Date not set",
    "en-US": "Date not set",
    "de-DE": "Datum nicht festgelegt",
    "da-DK": "Dato ikke angivet",
    "fr-FR": "Date non définie",
    "it-IT": "Data non impostata",
    "es-ES": "Fecha no establecida",
};

/** Localised "to now" suffix for open date ranges, e.g. "to now" / "bis heute". */
const RANGE_TO_NOW: Record<SupportedLocale, string> = {
    "en-GB": "to now",
    "en-US": "to now",
    "de-DE": "bis heute",
    "da-DK": "til nu",
    "fr-FR": "à aujourd'hui",
    "it-IT": "a oggi",
    "es-ES": "hasta hoy",
};

/** Relative time from now in the active locale, e.g. "3 days ago" / "vor 3 Tagen". */
export function formatRelativeFromNow(
    date: string | null | undefined,
    locale: SupportedLocale = DEFAULT_LOCALE
): string {
    if (!date) {
        if (locale === "de-DE") return "unbekannt";
        if (locale === "da-DK") return "ukendt";
        if (locale === "fr-FR") return "inconnu";
        if (locale === "it-IT") return "sconosciuto";
        if (locale === "es-ES") return "desconocido";
        return "unknown";
    }
    return dayjs(date).locale(DAYJS_LOCALE[locale]).fromNow();
}

const formatRelativeTime = (number: number, unit: ManipulateType) => {
    const time = dayjs().subtract(number, unit);

    if (number >= 0) {
        return time.fromNow();
    }
    return time.toNow();
};

const toNow = (date: string | undefined | null) => dayjs(date).toNow();

function ordinalDay(day: number): string {
    if (day >= 11 && day <= 13) {
        return `${day}th`;
    }
    switch (day % 10) {
        case 1:
            return `${day}st`;
        case 2:
            return `${day}nd`;
        case 3:
            return `${day}rd`;
        default:
            return `${day}th`;
    }
}

/** Patient-facing relative due time, e.g. "in 2 months". */
export function formatPatientDueRelative(date: string | undefined | null) {
    if (!date) {
        return "soon";
    }
    return dayjs(date).fromNow();
}

function roundPatientDueTime(dateTime: dayjs.Dayjs): dayjs.Dayjs {
    const roundedMinutes = Math.round(dateTime.minute() / 5) * 5;
    if (roundedMinutes === 60) {
        return dateTime.add(1, "hour").minute(0).second(0).millisecond(0);
    }
    return dateTime.minute(roundedMinutes).second(0).millisecond(0);
}

function formatPatientDueClockTime(dateTime: dayjs.Dayjs): string {
    const rounded = roundPatientDueTime(dateTime);
    return rounded.minute() === 0 ? rounded.format("ha").toLowerCase() : rounded.format("h:mma").toLowerCase();
}

/** Patient-facing absolute due time. Future dates omit the clock time; today includes it, rounded to 5 minutes. */
export function formatPatientDueAbsolute(date: string | undefined | null) {
    if (!date) {
        return "on a date to be confirmed";
    }
    const dateTime = dayjs(date);
    const now = dayjs();

    if (dateTime.isSame(now, "day")) {
        return `today at ${formatPatientDueClockTime(dateTime)}`;
    }

    const day = ordinalDay(dateTime.date());
    const month = dateTime.format("MMMM");
    const year = dateTime.format("YYYY");
    return `on ${day} ${month} ${year}`;
}

/** Long patient-facing due date, e.g. "Tuesday 21st July 2026" / "Dienstag, 21. Juli 2026". */
export function formatPatientLongDueDate(
    date: string | undefined | null,
    locale: SupportedLocale = DEFAULT_LOCALE
): string {
    if (!date) {
        return "";
    }

    const dateTime = dayjs(date).locale(DAYJS_LOCALE[locale]);

    if (locale === "de-DE") {
        return dateTime.format("dddd, D. MMMM YYYY");
    }

    if (locale === "da-DK") {
        return dateTime.format("dddd [den] D. MMMM YYYY");
    }

    if (locale === "en-US") {
        return dateTime.format("dddd, MMMM D, YYYY");
    }

    if (locale === "fr-FR") {
        return dateTime.format("dddd D MMMM YYYY");
    }

    if (locale === "it-IT" || locale === "es-ES") {
        return dateTime.format("dddd D MMMM YYYY");
    }

    return `${dateTime.format("dddd")} ${ordinalDay(dateTime.date())} ${dateTime.format("MMMM YYYY")}`;
}

export function formatDateTime(date: string | undefined | null, locale: SupportedLocale = DEFAULT_LOCALE) {
    if (!date) {
        return DATE_NOT_SET[locale];
    }
    const dateTime = dayjs(date).locale(DAYJS_LOCALE[locale]);
    if (locale === "en-US") {
        return `${dateTime.format("ddd, MMM D, YYYY, h:mm A")} ${dateTime.format("Z")}`;
    }
    return `${dateTime.format("ddd, DD MMM YYYY, HH:mm")} ${dateTime.format("Z")}`;
}

export function formatShortDateTime(date: string | undefined | null, locale: SupportedLocale = DEFAULT_LOCALE) {
    return formatLocaleShortDateTime(date, locale);
}

function formatDate(date: string | undefined | null, locale: SupportedLocale = DEFAULT_LOCALE) {
    if (!date) {
        return DATE_NOT_SET[locale];
    }
    if (locale === "en-US") {
        return dayjs(date).locale("en").format("MMM D, YYYY");
    }
    return dayjs(date).format("DD MMM YYYY");
}

function formatSupabaseDate(date?: string | null) {
    if (!date) {
        return dayjs().utc().format("YYYY-MM-DD HH:mm:ss.SSS[Z]");
    }
    return dayjs(date).utc().format("YYYY-MM-DD HH:mm:ss.SSS[Z]");
}

function formatISO(date: string | undefined | null) {
    if (!date) {
        return null;
    }
    return dayjs(date).toISOString();
}

const formatRange = (
    start: string | undefined | null,
    end: string | undefined | null,
    locale: SupportedLocale = DEFAULT_LOCALE
) => {
    if (!start) {
        return DATE_NOT_SET[locale];
    }
    if (!end) {
        return `${formatDate(start, locale)} ${RANGE_TO_NOW[locale]}`;
    }
    return `${formatDate(start, locale)} to ${formatDate(end, locale)}`;
};

const findEarliest = (times: any[], selector: string) => {
    try {
        return times.reduce((a: any, b: any) => (dayjs(a[selector]).isAfter(b[selector]) ? a : b));
    } catch (e) {
        console.error(e);
        return null;
    }
};

const composeDateTime = (date?: string, time?: string) => {
    if (!date && !time) return null;
    if (!date) return null;
    if (!time) return formatISO(`${dayjs(date).format("YYYY-MM-DD")} 12:00`);
    return formatISO(`${dayjs(date).format("YYYY-MM-DD")} ${time}`);
};

export const time = {
    formatRelativeTime,
    formatRelativeFromNow,
    formatDateTime,
    formatShortDateTime,
    formatDate,
    formatISO,
    formatSupabaseDate,
    formatPatientDueRelative,
    formatPatientDueAbsolute,
    formatPatientLongDueDate,
    toNow,
    formatRange,
    findEarliest,
    composeDateTime,
};
